import { useMemo, useState } from "react";

import type { ChainDetail } from "@/api";

/**
 * Cost calculator — pure-frontend interactive estimator.
 *
 * Backend doesn't (yet) carry a predicted cost; this tab is the legacy
 * app's "what would this chain net us at X $/mi?" form. Two user inputs:
 *   - target $/mi (the rep's working assumption)
 *   - deadhead penalty $/mi (cost of empty miles; subtracts from revenue)
 *
 * Math is intentionally simple — there's no fuel surcharge / driver-day
 * model here yet because we don't have those data points in the API.
 * Result is a per-chain summary + per-leg breakdown.
 */

interface CostCalculatorProps {
  chain: ChainDetail;
}

const DEFAULT_RATE = 2.5;        // $/mi
const DEFAULT_DEADHEAD = 1.2;    // $/mi cost

export function CostCalculator({ chain }: CostCalculatorProps): JSX.Element {
  const [ratePerMi, setRatePerMi] = useState(DEFAULT_RATE);
  const [deadheadPerMi, setDeadheadPerMi] = useState(DEFAULT_DEADHEAD);

  const result = useMemo(
    () => computeCost(chain, ratePerMi, deadheadPerMi),
    [chain, ratePerMi, deadheadPerMi],
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-[260px_minmax(0,1fr)] gap-6">
      <form
        className="card p-4 space-y-3"
        onSubmit={(e) => e.preventDefault()}
        aria-label="Cost assumptions"
      >
        <h3 className="font-semibold text-slate-900">Assumptions</h3>
        <NumberField
          id="cost-rate"
          label="Revenue per mile ($)"
          step={0.05}
          min={0}
          value={ratePerMi}
          onChange={setRatePerMi}
        />
        <NumberField
          id="cost-deadhead"
          label="Deadhead cost per mile ($)"
          step={0.05}
          min={0}
          value={deadheadPerMi}
          onChange={setDeadheadPerMi}
        />
        <button
          type="button"
          className="btn-secondary w-full"
          onClick={() => {
            setRatePerMi(DEFAULT_RATE);
            setDeadheadPerMi(DEFAULT_DEADHEAD);
          }}
        >
          Reset to defaults
        </button>
      </form>

      <div className="space-y-4">
        <div className="card p-5">
          <h3 className="text-sm font-medium text-slate-500 mb-2">
            Estimated total
          </h3>
          <div className="grid grid-cols-3 gap-3 text-sm">
            <Money label="Loaded revenue" value={result.loadedRevenue} positive />
            <Money
              label="Deadhead cost"
              value={result.deadheadCost}
              positive={false}
            />
            <Money
              label="Net"
              value={result.net}
              positive={result.net >= 0}
              emphasis
            />
          </div>
          <div className="mt-3 text-xs text-slate-500">
            Loaded miles: {Math.round(result.loadedMi).toLocaleString()} ·{" "}
            Deadhead miles: {Math.round(result.deadheadMi).toLocaleString()} ·{" "}
            Effective rate:{" "}
            {result.loadedMi > 0
              ? `$${(result.net / result.loadedMi).toFixed(2)}/mi`
              : "—"}
          </div>
        </div>

        <table className="w-full text-sm">
          <thead className="text-left text-xs text-slate-500">
            <tr>
              <th className="px-2 py-1">Leg</th>
              <th className="px-2 py-1 text-right">Loaded mi</th>
              <th className="px-2 py-1 text-right">Deadhead mi</th>
              <th className="px-2 py-1 text-right">Revenue</th>
              <th className="px-2 py-1 text-right">Cost</th>
              <th className="px-2 py-1 text-right">Net</th>
            </tr>
          </thead>
          <tbody>
            {result.perLeg.map((row) => (
              <tr key={row.seq} className="border-t border-slate-100">
                <td className="px-2 py-1.5 text-slate-700">
                  #{row.load_id}
                </td>
                <td className="px-2 py-1.5 text-right">
                  {Math.round(row.loadedMi).toLocaleString()}
                </td>
                <td className="px-2 py-1.5 text-right">
                  {Math.round(row.deadheadMi).toLocaleString()}
                </td>
                <td className="px-2 py-1.5 text-right text-emerald-700">
                  ${row.revenue.toFixed(0)}
                </td>
                <td className="px-2 py-1.5 text-right text-rose-700">
                  −${row.cost.toFixed(0)}
                </td>
                <td
                  className={
                    "px-2 py-1.5 text-right font-medium " +
                    (row.net >= 0 ? "text-slate-900" : "text-rose-700")
                  }
                >
                  ${row.net.toFixed(0)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─────────────────────────────────  Subcomponents  ──

interface NumberFieldProps {
  id: string;
  label: string;
  value: number;
  step: number;
  min?: number;
  onChange: (next: number) => void;
}

function NumberField({
  id,
  label,
  value,
  step,
  min,
  onChange,
}: NumberFieldProps): JSX.Element {
  return (
    <div>
      <label className="label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        type="number"
        inputMode="decimal"
        className="input"
        step={step}
        min={min}
        value={value}
        onChange={(e) => {
          const next = parseFloat(e.target.value);
          if (Number.isFinite(next)) onChange(next);
        }}
      />
    </div>
  );
}

function Money({
  label,
  value,
  positive,
  emphasis,
}: {
  label: string;
  value: number;
  positive: boolean;
  emphasis?: boolean;
}): JSX.Element {
  const color = positive
    ? emphasis
      ? "text-emerald-700"
      : "text-emerald-600"
    : emphasis
      ? "text-rose-700"
      : "text-rose-600";
  return (
    <div>
      <div className="text-xs text-slate-400">{label}</div>
      <div className={`text-xl font-semibold ${color}`}>
        {value < 0 ? "−" : ""}${Math.abs(value).toFixed(0)}
      </div>
    </div>
  );
}

// ─────────────────────────────────  Pure helpers  ──

export interface PerLegCost {
  seq: number;
  load_id: number;
  loadedMi: number;
  deadheadMi: number;
  revenue: number;
  cost: number;
  net: number;
}

export interface CostResult {
  loadedMi: number;
  deadheadMi: number;
  loadedRevenue: number;
  deadheadCost: number;
  net: number;
  perLeg: PerLegCost[];
}

/**
 * Per-leg loaded miles ≈ chain.total_miles - chain.total_deadhead_mi,
 * apportioned across legs by drive_time_min (rough but consistent with
 * the legacy app's lack of per-leg loaded-mile data).
 */
export function computeCost(
  chain: ChainDetail,
  ratePerMi: number,
  deadheadPerMi: number,
): CostResult {
  const totalLoaded = Math.max(0, chain.total_miles - chain.total_deadhead_mi);
  const totalDrive = chain.loads.reduce((s, l) => s + l.drive_time_min, 0);

  const perLeg: PerLegCost[] = chain.loads.map((l) => {
    const share = totalDrive > 0 ? l.drive_time_min / totalDrive : 0;
    const loadedMi = totalLoaded * share;
    const deadheadMi = l.deadhead_mi ?? 0;
    const revenue = loadedMi * ratePerMi;
    const cost = deadheadMi * deadheadPerMi;
    return {
      seq: l.seq,
      load_id: l.load_id,
      loadedMi,
      deadheadMi,
      revenue,
      cost,
      net: revenue - cost,
    };
  });

  const loadedRevenue = perLeg.reduce((s, r) => s + r.revenue, 0);
  const deadheadCost = perLeg.reduce((s, r) => s + r.cost, 0);
  return {
    loadedMi: totalLoaded,
    deadheadMi: chain.total_deadhead_mi,
    loadedRevenue,
    deadheadCost,
    net: loadedRevenue - deadheadCost,
    perLeg,
  };
}
